<script lang="ts" setup>
import ChannelIcon from '@/components/channels/ChannelIcon.vue'
import { app } from '@/composition/app'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'
import {
  BookOpen01Icon,
  CalendarDateIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UISkeleton, UITextSmMedium } from '@gohighlevel/ghl-ui'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { MentionType } from '../mention-helper'

const props = defineProps({
  items: {
    type: Array<any>,
    required: true,
  },
  command: {
    type: Function,
    required: true,
  },
})

const selectedIndex = ref(0)
const itemRefs = ref<HTMLElement[]>([])
const itemsContainer = ref<HTMLElement>()
const { t } = useI18n()

// Group items by type
const groupedItems = computed(() => {
  const channels = props.items.filter(
    item => item.type === MentionType.CHANNEL_MENTION
  )
  const events = props.items.filter(
    item => item.type === MentionType.EVENTS_MENTION
  )
  const courses = props.items.filter(
    item => item.type === MentionType.COURSES_MENTION
  )

  const groups: Array<{
    title: string
    items: any[]
    type: string
  }> = []

  if (courses.length > 0) {
    groups.push({
      title: t('common.courses'),
      items: courses,
      type: MentionType.COURSES_MENTION,
    })
  }
  if (events.length > 0) {
    groups.push({
      title: t('common.events'),
      items: events,
      type: MentionType.EVENTS_MENTION,
    })
  }

  if (channels.length > 0) {
    groups.push({
      title: t('common.channels'),
      items: channels,
      type: MentionType.CHANNEL_MENTION,
    })
  }

  return groups
})

const flatItems = computed(() => {
  return props.items
})

watch(
  () => props.items,
  () => {
    selectedIndex.value = 0
  }
)

function onKeyDown({ event }) {
  if (isLoading.value) {
    return false
  }
  if (event.key === 'ArrowUp') {
    upHandler()
    return true
  }
  if (event.key === 'ArrowDown') {
    downHandler()
    return true
  }
  if (event.key === 'Enter') {
    enterHandler()
    return true
  }
  return false
}

function upHandler() {
  selectedIndex.value = selectedIndex.value - 1
  if (selectedIndex.value < 0) {
    selectedIndex.value = flatItems.value.length - 1
  }
  scrollToSelected()
}

function downHandler() {
  selectedIndex.value = selectedIndex.value + 1
  if (selectedIndex.value > flatItems.value.length - 1) {
    selectedIndex.value = 0
  }
  scrollToSelected()
}

function enterHandler() {
  selectItem(selectedIndex.value)
}

const eventTypeMap = {
  [MentionType.CHANNEL_MENTION]: GAEventTypes.CHANNEL_MENTIONED,
  [MentionType.EVENTS_MENTION]: GAEventTypes.EVENT_MENTIONED,
  [MentionType.COURSES_MENTION]: GAEventTypes.COURSE_MENTIONED,
}

function scrollToSelected() {
  nextTick(() => {
    const selectedElement = itemRefs.value[selectedIndex.value]
    if (selectedElement) {
      if (selectedIndex.value === 0) {
        itemsContainer.value?.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
        return
      } else {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }
  })
}

function selectItem(index) {
  // loading state is not a valid state for selection
  if (isLoading.value) {
    return false
  }
  const item = flatItems.value[index]
  if (item) {
    trackCustomGAEvents(eventTypeMap[item.type], {
      type: item.type,
    })
    props.command(item)
  }
}

function getGlobalIndex(groupIndex: number, itemIndex: number) {
  let globalIndex = 0
  for (let i = 0; i < groupIndex; i++) {
    globalIndex += groupedItems?.value[i]?.items?.length || 0
  }
  return globalIndex + itemIndex
}

const isLoading = computed(() => {
  if (props.items.length === 1 && props.items[0].type === 'loading') {
    return true
  }
  return false
})

const sectionHeaders = [
  t('common.courses'),
  t('common.events'),
  t('common.channels'),
]

defineExpose({ onKeyDown })
</script>

<template>
  <div>
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="items tag-wrapper w-[316px]"
      :class="app.state.isMobile ? 'max-h-[200px]' : 'max-h-[400px]'"
    >
      <div v-for="header in sectionHeaders" :key="header" class="group">
        <div
          class="section-header border-b border-gray-100 px-4 py-2 text-xs font-semibold tracking-wide text-communities-font-secondary"
        >
          {{ header }}
        </div>
        <div
          class="flex flex-row items-center px-4 py-2 text-communities-font-secondary"
        >
          <UISkeleton height="24px" width="38px" circle class="flex-shrink-0" />
          <div class="ml-2 flex items-center">
            <UISkeleton height="24px" width="250px" rounded />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="groupedItems.length"
      ref="itemsContainer"
      class="items tag-wrapper w-[316px]"
      :class="app.state.isMobile ? 'max-h-[210px]' : 'max-h-[450px]'"
    >
      <div
        v-for="(group, groupIndex) in groupedItems"
        :key="group.title"
        class="group"
      >
        <div
          class="section-header border-b border-gray-100 px-4 pb-2 pl-2 font-semibold tracking-wide text-communities-font-secondary"
          :class="groupIndex === 0 ? 'pt-4' : ''"
        >
          {{ group.title }}
        </div>

        <!-- Group Items -->
        <div
          v-for="(item, itemIndex) in group.items"
          :key="item.id ?? item.label"
          :ref="
            (el) => {
              const globalIndex = getGlobalIndex(groupIndex, itemIndex)
              if (el) itemRefs[globalIndex] = el as HTMLElement
            }
          "
          :class="{
            'is-selected':
              getGlobalIndex(groupIndex, itemIndex) === selectedIndex,
            'mobile-item': app.state.isMobile,
          }"
          class="flex h-[24px] cursor-pointer flex-row items-center px-4 py-3 pl-2 text-communities-font-primary hover:bg-gray-50"
          role="listbox"
          :aria-label="t('common.ariaLabels.mentionItem')"
          tabindex="0"
          @mouseover="selectedIndex = getGlobalIndex(groupIndex, itemIndex)"
          @mouseleave="selectedIndex = -1"
          @click="selectItem(getGlobalIndex(groupIndex, itemIndex))"
          @keyup.enter="selectItem(getGlobalIndex(groupIndex, itemIndex))"
        >
          <ChannelIcon
            v-if="item.type === MentionType.CHANNEL_MENTION"
            :icon-size="16"
            :emoji-size="16"
            :channel="item"
            :showing-in-sidebar="false"
            :show-lock-icon="false"
          />
          <CalendarDateIcon
            v-if="item.type === MentionType.EVENTS_MENTION"
            class="h-4 w-5 flex-shrink-0 text-communities-font-primary"
          />
          <BookOpen01Icon
            v-if="item.type === MentionType.COURSES_MENTION"
            class="h-4 w-5 flex-shrink-0 text-communities-font-primary"
          />
          <UITextSmMedium class="ml-2 truncate font-semibold">
            {{ item.label }}
          </UITextSmMedium>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.items {
  position: relative;
  border-radius: 0.5rem;
  background: var(--background);
  font-size: 14px;
  overflow-y: auto;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0px 10px 20px rgba(0, 0, 0, 0.1);
}
.items .mobile-item {
  height: 230px;
  height: 16px;
}

.section-header {
  background-color: var(--background);
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 14px;
  padding-top: 16px;
}

.is-selected {
  background-color: var(--fill);
}

.group:not(:last-child) {
  border-bottom: 1px solid var(--border);
}
.tag-wrapper {
  padding: 8px;
  padding-top: 0px;
  box-sizing: content-box;
}
</style>
